'use client';

import { traceEvent } from '@wk/wk-gatherer';
import { Button } from '@wk/wk-uni-lib';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useContext } from 'react';

import LeadButton from '@/components/LeadButton';
import TrustpilotBar from '@/components/TrustpilotBar';
import { GlobalLoginContext } from '@/context/GlobalLoginContest';

/**
 * 约课区模块
 * 展示 CTA 引导用户预约试听课
 */
const BookSection: React.FC = () => {
  const { t, i18n } = useTranslation('wukong-reviews');
  const isZh = i18n.language === 'zh';
  const { isLogin } = useContext(GlobalLoginContext);

  // 约课按钮埋点
  const handleBookClassTrace = useCallback(() => {
    traceEvent({
      data: {
        _event: 'C_WallofLove_BookClass',
        dsSource: '用户之声-约课版块',
        loginStatus: isLogin ? '已登' : '未登',
      },
    });
  }, [isLogin]);

  return (
    <section className="relative flex w-full flex-col items-center overflow-hidden bg-white lg:py-[70px] xl:py-[90px]">
      {/* 移动端布局 */}
      <div className="relative flex w-full flex-col items-center lg:hidden">
        {/* 标题区域 */}
        <div className="z-10 flex flex-col items-center space-y-[44px] px-4 pt-10">
          <h2
            className={`text-center text-[25px] font-black leading-normal lg:text-[34px] xl:text-[48px] ${isZh ? 'font-sourceHanSans' : ''}`}>
            <span className="text-text-primary">{t('约课-标题1')} </span>
            <br />
            <span className="text-brand-red-100">{t('约课-标题2')}</span>
          </h2>
          <TrustpilotBar size="md" />
        </div>

        {/* 背景图区域 - 包含学生照片 */}
        <div className="-mt-[54px] flex w-full justify-center">
          <Image
            src="/images/wukong-reviews/book-bg-phone.png"
            alt=""
            width={400}
            height={368}
            className="w-full object-cover object-center"
            quality={100}
            unoptimized
          />
        </div>

        {/* CTA 按钮 */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className="z-10 pb-15 pt-3" onClick={handleBookClassTrace}>
          <LeadButton
            asCustomButton
            callbackUrl={
              typeof window !== 'undefined' ? window.location.href : ''
            }
            traceName="O_WallOfLove_BookSection_CTAClick">
            <Button
              size="large"
              className="h-[52px] px-[30px] text-[20px] font-bold">
              {t('约课-按钮')}
            </Button>
          </LeadButton>
        </div>
      </div>

      {/* PC端布局 */}
      <div className="relative hidden w-full lg:block lg:h-[660px] xl:h-[743px]">
        {/* 背景图 - 绝对定位，使用 object-cover 填满整个区域 */}
        <Image
          src="/images/wukong-reviews/book-bg.png"
          alt=""
          fill
          className="object-cover object-center xl:hidden"
          quality={100}
        />
        <Image
          src="/images/wukong-reviews/book-bg-xl.png"
          alt=""
          fill
          className="hidden object-cover object-center xl:block"
          quality={100}
        />
        {/* 中间内容 - 相对定位在顶层 */}
        <div className="relative z-10 flex h-full flex-col items-center justify-center lg:mt-[50px] xl:mt-[40px]">
          {/* 标题 */}
          <h2
            className={`flex flex-col text-center font-black leading-normal lg:text-[34px] xl:text-[48px] ${isZh ? 'font-sourceHanSans' : ''}`}>
            <span className="text-text-primary">{t('约课-标题1')}</span>
            <span className="mt-3 text-brand-red-100">{t('约课-标题2')}</span>
          </h2>

          {/* Trustpilot 评分 */}
          <div className="mt-10">
            <TrustpilotBar size="md" />
          </div>

          {/* CTA 按钮 */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div className="mt-20" onClick={handleBookClassTrace}>
            <LeadButton
              asCustomButton
              callbackUrl={
                typeof window !== 'undefined' ? window.location.href : ''
              }
              traceName="O_WallOfLove_BookSection_CTAClick">
              <Button
                size="large"
                className="font-bold lg:h-[55px] lg:px-12 lg:text-[18px] xl:h-[77px] xl:px-[64px] xl:text-[24px]">
                {t('约课-按钮')}
              </Button>
            </LeadButton>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookSection;
